import { Component, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, map } from 'rxjs';

import { Account } from '../../../auth/models/account.model';
import { Role } from '../../../auth/models/role.model';
import { User } from '../../../auth/models/user.model';
import { Company } from '../../../models/company.model';
import { TeamMember } from '../../../models/teamMember.model';
import { TeamService } from '../../../services/team.service';
import { ManagersService } from '../../services/managers.service';

@Component({
  selector: 'ag-team-managers',
  templateUrl: './team-managers.component.html',
  styleUrls: ['./team-managers.component.scss']
})
export class TeamManagersComponent implements OnInit {

  @Input() public company: Company;
  @Input() public user: User;
  @Input() public account: Account;
  @ViewChild('managersModal', { static: false }) managersModal: any;

  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;
  public selected: TeamMember[] = [];
  public deletedMember = new EventEmitter();

  // Observables for async pipe
  public managers$: Observable<TeamMember[]>;
  public roles$: Observable<Role[]>;
  public viewData$: Observable<{ managers: TeamMember[]; roles: Role[] }>;

  // Cached for methods that need them
  private currentManagers: TeamMember[];

  /** @ignore */
  constructor(
    private managerService: ManagersService,
    private teamService: TeamService,
    private translateService: TranslateService
  ) { }

  /** @ignore */
  ngOnInit(): void {
    this.loadData();
  }

  public loadData(): void {
    const myRepresentatives = this.managerService.getMyRepresentatives(this.company.id);
    const representatives = this.managerService.getRepresentatives(this.company.id);

    this.managers$ = forkJoin([myRepresentatives, representatives]).pipe(
      map(managers => {
        const allManagers = [...managers[0], ...managers[1]];
        this.currentManagers = allManagers;
        this.translateManagersRoles(allManagers);
        return allManagers;
      })
    );

    this.roles$ = this.managerService.getRoles();

    this.viewData$ = forkJoin([this.managers$, this.roles$]).pipe(
      map(([managers, roles]) => ({ managers, roles }))
    );
  }

  public iRepresent(member: TeamMember): boolean {
    return member.parent && member.parent?.company.id === this.company.id
  }

  public acceptInvitation(invitationId: number): void {
    if (!this.processing) {
      this.processing = true;
      this.managerService.acceptInvitation(this.company.id, invitationId).subscribe(() => {
        this.loadData();
        this.processing = false;
      });
    }
  }

  /** Removes the specified [[Manager]]. */
  public deleteManager(member: TeamMember): void {
    this.processing = true;
    this.teamService.delete(member).subscribe(() => {
      this.deletedMember.emit();
      this.loadData();
      this.processing = false;
    });
  }

  public edit(member: TeamMember): void {
    this.processing = true;
    this.teamService.edit(member).subscribe(() => {
      this.processing = false;
    });
  }

  private translateManagersRoles(managers: TeamMember[]): void {
    managers.forEach(manager => {
      const roleName = `ACCOUNT.ROLES.${manager.role.slug.replace('-', '_')}`
      manager.role.name = this.translateService.instant(roleName);
    })
  }
}
